import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";

/**
 * Salesforce connect/status/disconnect API.
 *
 * Talks to the real Express middleware at `/api/salesforce/*`, which proxies
 * to the CRM service (Infynd-CL-CRM-Service). Mirrors hubspotApi.ts
 * endpoint-for-endpoint (accounts and contacts) — see
 * that file's header for why this, not the old `crmApi`, is what Salesforce
 * should be wired to.
 */

export type SalesforceObjectType = "accounts" | "contacts";

export interface SalesforceStatus {
  connected: boolean;
  instance_url: string | null;
  connected_at: string | null;
  /** See HubspotStatus's docstring — same meaning here. */
  needs_reauth: boolean;
}

/** One writable Salesforce field, as the CRM service's columns endpoint returns it. */
export interface SalesforceColumn {
  /** Salesforce's internal field name — what gets stored as the mapping value. */
  name: string;
  label: string;
  type: string;
  options: { label: string; value: string }[];
}

export interface SalesforceMappableField {
  key: string;
  label: string;
  type: string;
  isRequired: boolean;
  crmFieldValue: string | null;
}

export interface SalesforceSettings {
  connection: {
    allowContactExport: boolean;
    allowCompanyExport: boolean;
    enableIndicator: boolean;
    excludeFromBulk: boolean;
    indicator: {
      company: SalesforceMappableField[];
      contact: SalesforceMappableField[];
    };
  };
  fieldMappings: {
    company: SalesforceMappableField[];
    contact: SalesforceMappableField[];
  };
}

/** One Salesforce list view — read-only; see salesforce_field_catalog.py's
 * sibling module docstring for why there's no "add to this" action for it. */
export interface SalesforceSegment {
  value: string;
  label: string;
  totalRecords: number;
  matchedRecords: number;
  status: string;
  lastSyncedAt: string | null;
}

/** One Campaign — the add-to-segment target for Contact export. */
export interface SalesforceCampaign {
  value: string;
  label: string;
}

export interface SalesforceExportResult {
  created: number;
  updated: number;
  results: {
    infynd_id: string;
    salesforce_id?: string;
    status: "created" | "updated" | "skipped" | "error";
    reason?: string;
  }[];
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const salesforceApi = createApi({
  reducerPath: "salesforceApi",
  baseQuery: axiosBaseQuery("/api/salesforce"),
  tagTypes: ["SalesforceStatus", "SalesforceSettings"],
  endpoints: (builder) => ({
    getSalesforceStatus: builder.query<SalesforceStatus, void>({
      query: () => "/status",
      transformResponse: (response: Envelope<SalesforceStatus>) => response.data,
      providesTags: ["SalesforceStatus"],
    }),

    getSalesforceAuthorizeUrl: builder.mutation<{ url: string }, void>({
      query: () => ({ url: "/authorize-url", method: "GET" }),
      transformResponse: (response: Envelope<{ url: string }>) => response.data,
    }),

    disconnectSalesforce: builder.mutation<void, void>({
      query: () => ({ url: "/disconnect", method: "POST" }),
      invalidatesTags: ["SalesforceStatus"],
    }),

    getSalesforceColumns: builder.query<SalesforceColumn[], { objectType: SalesforceObjectType }>({
      query: ({ objectType }) => `/${objectType}/columns`,
      transformResponse: (response: Envelope<{ columns: SalesforceColumn[] }>) => response.data.columns,
    }),

    getSalesforceSegments: builder.query<SalesforceSegment[], { objectType: SalesforceObjectType }>({
      query: ({ objectType }) => `/${objectType}/segments`,
      transformResponse: (response: Envelope<{ segments: SalesforceSegment[] }>) => response.data.segments,
    }),

    getSalesforceCampaigns: builder.query<SalesforceCampaign[], void>({
      query: () => "/campaigns",
      transformResponse: (response: Envelope<{ campaigns: SalesforceCampaign[] }>) => response.data.campaigns,
    }),

    getSalesforceSettings: builder.query<SalesforceSettings, void>({
      query: () => "/settings",
      transformResponse: (response: Envelope<SalesforceSettings>) => response.data,
      providesTags: ["SalesforceSettings"],
    }),

    saveSalesforceSettings: builder.mutation<void, SalesforceSettings>({
      query: (body) => ({ url: "/settings", method: "PUT", body }),
      invalidatesTags: ["SalesforceSettings"],
    }),

    autoMapSalesforceSettings: builder.mutation<SalesforceSettings, SalesforceSettings>({
      query: (body) => ({ url: "/settings/auto-map", method: "POST", body }),
      transformResponse: (response: Envelope<SalesforceSettings>) => response.data,
    }),

    exportToSalesforce: builder.mutation<
      SalesforceExportResult,
      { objectType: SalesforceObjectType; selectedIds: string[] }
    >({
      query: ({ objectType, selectedIds }) => ({
        url: `/export/${objectType}`,
        method: "POST",
        body: { selectedIds },
      }),
      transformResponse: (response: Envelope<SalesforceExportResult>) => response.data,
    }),
  }),
});

export const {
  useGetSalesforceStatusQuery,
  useGetSalesforceAuthorizeUrlMutation,
  useDisconnectSalesforceMutation,
  useGetSalesforceColumnsQuery,
  useGetSalesforceSegmentsQuery,
  useGetSalesforceCampaignsQuery,
  useGetSalesforceSettingsQuery,
  useSaveSalesforceSettingsMutation,
  useAutoMapSalesforceSettingsMutation,
  useExportToSalesforceMutation,
} = salesforceApi;

export default salesforceApi;
